/**
 * Remove a staff member (DNKADM-0481 / DNKAPI-0697) — the confirm dialog behind the Staff
 * list's Remove action. OWNER/ADMIN only; the host decides who sees the action.
 *
 * Remove ARCHIVES the staffer (`DELETE /v2/admin/staff/{id}`): they leave every list, picker
 * and pool, and their past bookings keep their name. Three outcomes the dialog itself shows:
 *  - **`409 STAFF_HAS_UPCOMING_BOOKINGS`** — nothing was removed. The bookings in the way are
 *    listed, each linking to the calendar's `/reservations?reservation=<uuid>` deep link, so
 *    the desk can move them first and try again.
 *  - **Removed, login still active** — when "Also remove their login" was ticked and the
 *    Team & access revoke (the existing `PUT …/platform-members/{userId}/status` DISABLED
 *    write) failed, for ANY reason — including saas-auth's `409 LAST_OWNER_PROTECTED` when
 *    the login is the tenant's last owner. The staffer STAYS removed (no rollback); the copy
 *    says the login is still active and points to Team & access.
 *  - **Any other failure** — the server's (brand-scrubbed) message, or a neutral line.
 * A clean remove (with or without the login) closes through `onRemoved` and the host toasts.
 *
 * The login checkbox shows only when the coach record has a linked login (`authUserId`) AND the
 * revoke can succeed: the Team & access status write is for an owner or human platform
 * support (DNKADM-0167 / DNKADM-0483 — the operator's `canManageTeam`, the same flag that
 * screen gates on; dink-api enforces it as DNKAPI-0698), and the server refuses a
 * self-revoke (`SELF_MODIFICATION_FORBIDDEN`; matched like Team & access's `isSelfMember`, on
 * the JWT `sub` = `authUserId`). Otherwise a plain line says the login stays active and the
 * status write is never sent. The revoke runs strictly AFTER the remove succeeds — a refused
 * remove never touches the login.
 *
 * **Default (F-292, Peyton 2026-10-28).** Turning the login off also ends any MEMBER access on
 * it, so the checkbox defaults ON only when the login provably has none. That is read from the
 * existing Team & access roster (`GET /v2/admin/platform-members`, loaded only when the
 * checkbox is offered): a person with member access is a second roster row with the SAME
 * `userId` and `kind: 'CUSTOMER'` (DNKAPI-0517). Any CUSTOMER row → member access; rows that
 * all say what they are and none is CUSTOMER → none; a failed roster, a row with no `kind`, or
 * a login missing from the roster → unknown, treated as member access (default OFF + warning).
 * While the roster loads the box is unticked and held, so a fast click can only keep the login.
 *
 * F-290: removing a coach also clears them as any member's assigned coach; the confirm copy
 * says so.
 *
 * A `404` on the remove means the staffer is already gone (removed elsewhere): the host
 * closes, refetches and says so (`already-removed`).
 *
 * Every backend string is scrubbed at this render boundary (`serverMessage` for the error,
 * `scrubBrand` for each booking's names). Mobile: full-screen below `sm`.
 */
import { useCallback, useState } from 'react';
import { ApiError } from '@/api/errors';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  getPlatformMembers,
  setPlatformMemberStatus,
  type PlatformMember,
} from '@/api/platformMembers';
import type { BookingConflict } from '@/api/reservations';
import { serverMessage } from '@/api/serverMessage';
import { removeStaff, staffDisplayName, type V2Staff } from '@/api/staff';
import { deriveOperator } from '@/auth/authContextValue';
import { getClaims } from '@/auth/authStore';
import { useResource } from '@/hooks/useResource';
import { resolveTokens } from '@/theme';
import { parseStaffUpcomingBookings } from './staffError';
import { whenAndWhere, whoAndWhat } from './bookingLines';

/** The checkbox label (pinned by the tests). */
export const REMOVE_LOGIN_LABEL = 'Also remove their login';

/** Shown instead of the checkbox to an ADMIN: the revoke is for an owner or platform support. */
export const LOGIN_STAYS_OWNER_ONLY =
  'Their login stays active. An owner can turn it off in Team & access.';

/** Shown instead of the checkbox when the linked login is the operator's own. */
export const LOGIN_STAYS_SELF = 'This is your login, so it stays active.';

/** F-290: the member-facing consequence of removing a coach. */
export const MEMBERS_LOSE_COACH = 'Members assigned to them will show no coach.';

/** F-292: the login also has member access, so turning it off ends that too. */
export const LOGIN_HAS_MEMBER_ACCESS =
  'This login also has member access. Turning it off ends their member access too.';

/** F-292: member access could not be checked — the safe side, said plainly. */
export const LOGIN_MEMBER_ACCESS_UNKNOWN =
  'If this login also has member access, turning it off ends that too.';

/** Whether a login carries member access, as the roster tells it. */
export type MemberAccess = 'yes' | 'no' | 'unknown';

/**
 * Read member access for `userId` off the Team & access roster (see the module doc): any
 * CUSTOMER row → `yes`; rows that all carry a `kind` and none is CUSTOMER → `no`; no row, or a
 * row with no `kind` (pre-0517) → `unknown`.
 */
export function loginMemberAccess(members: PlatformMember[], userId: number): MemberAccess {
  const rows = members.filter((m) => m.userId === userId);
  if (rows.some((m) => m.kind === 'CUSTOMER')) return 'yes';
  if (rows.length === 0 || rows.some((m) => m.kind === null)) return 'unknown';
  return 'no';
}

/**
 * What the remove did, so the host can say it in one toast. `already-removed` is a `404`:
 * the staffer was removed elsewhere, nothing changed here.
 */
export type RemoveStaffOutcome = 'removed' | 'removed-and-login-off' | 'already-removed';

export interface RemoveStaffDialogProps {
  /** The staffer being removed. The host mounts the dialog per target (`key`). */
  staff: V2Staff;
  /** Close without (further) change. */
  onClose: () => void;
  /** A clean remove: the host closes the dialog, refetches and toasts. */
  onRemoved: (outcome: RemoveStaffOutcome) => void;
  /**
   * The staffer WAS removed but the login revoke failed: the host refetches and leaves the
   * dialog open, which now shows the "login still active" copy.
   */
  onRemovedLoginStillActive: () => void;
}

type Phase =
  | { kind: 'confirm' }
  | { kind: 'blocked'; bookings: BookingConflict[] }
  | { kind: 'error'; message: string }
  | { kind: 'login-still-active' };

function UpcomingBookings({
  bookings,
  onFollowLink,
}: {
  bookings: BookingConflict[];
  onFollowLink: () => void;
}) {
  const t = resolveTokens(useTheme());
  if (bookings.length === 0) return null;
  return (
    <Stack component="ul" aria-label="Upcoming bookings" sx={{ listStyle: 'none', p: 0, m: 0, gap: 1 }}>
      {bookings.map((b, index) => (
        <Stack
          component="li"
          key={b.reservationUuid ?? `booking-${index}`}
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
            px: 1.5,
            py: 1.25,
            border: `1px solid ${t.lineSoft}`,
            borderRadius: 1.5,
            bgcolor: t.surface2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
            <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{whoAndWhat(b)}</Typography>
            <Typography variant="body2" sx={{ color: t.text2, overflowWrap: 'anywhere' }}>
              {whenAndWhere(b)}
            </Typography>
          </Box>
          {b.reservationUuid !== null && (
            <Link
              component={RouterLink}
              to={`/reservations?reservation=${encodeURIComponent(b.reservationUuid)}`}
              onClick={onFollowLink}
              sx={{ fontSize: 14, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              View booking
            </Link>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

export function RemoveStaffDialog({
  staff,
  onClose,
  onRemoved,
  onRemovedLoginStillActive,
}: RemoveStaffDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const name = staffDisplayName(staff);
  const hasLogin = staff.authUserId !== null;
  // Whether the login revoke can succeed for THIS operator (see the module doc).
  const claims = getClaims();
  // OWNER, or a human platform support session (DNKADM-0483) — derived once in
  // `deriveOperator`, never re-read from the claims here.
  const canManageTeam = deriveOperator(claims)?.canManageTeam ?? false;
  const claimSub = typeof claims?.sub === 'string' ? claims.sub.trim() : '';
  const isOwnLogin = hasLogin && claimSub !== '' && claimSub === String(staff.authUserId);
  const offerRevoke = hasLogin && canManageTeam && !isOwnLogin;
  const loginNote = !hasLogin || offerRevoke ? null : isOwnLogin ? LOGIN_STAYS_SELF : LOGIN_STAYS_OWNER_ONLY;
  // The roster, read only when the checkbox is offered (`canManageTeam`); otherwise never fetched.
  const roster = useResource(
    useCallback(
      () => (offerRevoke ? getPlatformMembers() : Promise.resolve(null)),
      [offerRevoke],
    ),
    [offerRevoke],
  );
  const accessLoading = offerRevoke && roster.status === 'loading';
  const memberAccess: MemberAccess =
    roster.status === 'ready' && roster.data && staff.authUserId !== null
      ? loginMemberAccess(roster.data.members, staff.authUserId)
      : 'unknown';
  // The operator's own choice once they touch the box; until then the F-292 default.
  const [removeLoginChoice, setRemoveLoginChoice] = useState<boolean | null>(null);
  const removeLogin = !accessLoading && (removeLoginChoice ?? memberAccess === 'no');
  const accessWarning =
    !offerRevoke || accessLoading || memberAccess === 'no'
      ? null
      : memberAccess === 'yes'
        ? LOGIN_HAS_MEMBER_ACCESS
        : LOGIN_MEMBER_ACCESS_UNKNOWN;
  const [phase, setPhase] = useState<Phase>({ kind: 'confirm' });
  const [submitting, setSubmitting] = useState(false);

  async function handleRemove() {
    setSubmitting(true);
    try {
      await removeStaff(staff.id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        onRemoved('already-removed');
        return;
      }
      const bookings = parseStaffUpcomingBookings(err);
      setPhase(
        bookings !== null
          ? { kind: 'blocked', bookings }
          : { kind: 'error', message: serverMessage(err) ?? 'Couldn’t remove the staff member.' },
      );
      setSubmitting(false);
      return;
    }
    // Removed. The login revoke runs only now, and its failure never undoes the remove.
    if (offerRevoke && removeLogin && staff.authUserId !== null) {
      try {
        await setPlatformMemberStatus(staff.authUserId, 'DISABLED');
      } catch {
        setSubmitting(false);
        setPhase({ kind: 'login-still-active' });
        onRemovedLoginStillActive();
        return;
      }
      onRemoved('removed-and-login-off');
      return;
    }
    onRemoved('removed');
  }

  const done = phase.kind === 'login-still-active';

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      aria-labelledby="remove-staff-title"
    >
      <DialogTitle id="remove-staff-title">
        {done ? `${name} was removed` : `Remove ${name}?`}
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 2 }}>
          {phase.kind === 'login-still-active' ? (
            <Alert severity="warning">
              Their login is still active. Turn it off in{' '}
              <Link component={RouterLink} to="/platform-members" onClick={onClose}>
                Team &amp; access
              </Link>
              .
            </Alert>
          ) : (
            <>
              <Typography variant="body2">
                {name} comes off the staff list, schedules and booking pickers. Their past bookings
                stay.
              </Typography>
              <Typography variant="body2">{MEMBERS_LOSE_COACH}</Typography>
              {loginNote !== null && <Typography variant="body2">{loginNote}</Typography>}
              {offerRevoke && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={removeLogin}
                      onChange={(e) => setRemoveLoginChoice(e.target.checked)}
                      disabled={submitting || accessLoading}
                    />
                  }
                  label={REMOVE_LOGIN_LABEL}
                />
              )}
              {accessWarning !== null && (
                <Typography variant="body2" sx={{ color: 'warning.main' }}>
                  {accessWarning}
                </Typography>
              )}
            </>
          )}
          {phase.kind === 'blocked' && (
            <>
              <Alert severity="warning">
                {name} has upcoming bookings, so nothing was removed. Move or cancel them, then try
                again.
              </Alert>
              <UpcomingBookings bookings={phase.bookings} onFollowLink={onClose} />
            </>
          )}
          {phase.kind === 'error' && <Alert severity="error">{phase.message}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {done ? (
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => void handleRemove()}
              disabled={submitting}
            >
              {phase.kind === 'blocked' ? 'Try again' : 'Remove'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
